import { z } from 'zod';
import {
  DEFAULT_CONFIG,
  DEFAULT_TAKE_PROFIT_CONFIG,
  KALSHI_TAKER_FEES,
  calculateAllocation,
  earlyExitTradePnl,
  heldTradePnl,
  takeProfitTarget,
  tradeFees,
  validContractPrice,
  type FeeModel,
  type TakeProfitConfig,
} from '@whaletail/signal-engine';
import type { Btc15PredictorResponse } from './btc15Types';
import {
  applyOfficialResultsTo,
  proxyActual,
  type OfficialRoundResult,
  type SettlementSource,
} from './btc15OfficialSettlement';

export const TAKEPROFIT_HISTORY_SCHEMA_VERSION = 1;
const MAX_HISTORY = 1200;

/**
 * "V1 + take-profit": the same V1 heuristic call and probability as
 * btc15History.ts's own `Btc15PredictionRecord`, but with an alternate exit
 * rule — sell early once the contract's bid has gained enough over entry
 * (see packages/signal-engine/src/takeProfit.ts), rather than always
 * riding to the round's settlement. This is a strategy variant of V1, not
 * an independent model: `action`/`probability` are V1's own, unchanged.
 *
 * Server-authoritative only, by design: an early exit can only be observed
 * by polling Kalshi's live bid during the round (btc15-takeprofit-scheduled.ts),
 * which the browser never does directly (this app only ever talks to its
 * own /api/* functions). A client-side local copy could silently miss an
 * early exit and diverge from the truth, so unlike V1/V2/V3 there is no
 * client-side recording path here — the client only ever reads this back
 * (see useBtc15TakeProfitHistory.ts).
 */
export interface TakeProfitRecord {
  roundId: string;
  createdMs: number;
  roundCloseMs: number;
  roundOpenPrice: number;
  action: 'UP' | 'DOWN';
  probability: number;
  /** The gain (dollars) required to exit early, computed once at entry from `probability` — stored for traceability even though it's derivable, since a future config change shouldn't silently reinterpret an already-recorded round. */
  targetGain: number;
  contractEntryPrice: number | null;
  contractQuoteSource: 'live_ask' | 'unavailable';
  exitedEarly: boolean;
  /** The bid price this position was actually sold at, if it exited early. */
  exitPrice: number | null;
  exitMs: number | null;
  settled: boolean;
  settledMs: number | null;
  /** Only set when held to settlement — an early exit resolves via `exitPrice` instead. */
  closePrice: number | null;
  actual: 'UP' | 'DOWN' | null;
  /** True whenever this position closed at a gain — always true for an early exit (that's what triggers one), or the settlement outcome otherwise. */
  won: boolean | null;
  /** Kalshi's target line (`floor_strike`) for this round, when known. */
  targetLine?: number | null;
  /** Where a held-to-expiry `actual`/`won` came from. Absent on older records, which were proxy-settled. */
  settlementSource?: SettlementSource;
}

export interface TakeProfitHistory {
  schemaVersion: number;
  predictions: TakeProfitRecord[];
}

export function emptyTakeProfitHistory(): TakeProfitHistory {
  return { schemaVersion: TAKEPROFIT_HISTORY_SCHEMA_VERSION, predictions: [] };
}

/** Sorts oldest-first and caps to `MAX_HISTORY`, stamping the current schema version. */
export function trimTakeProfitHistory(history: TakeProfitHistory): TakeProfitHistory {
  return {
    schemaVersion: TAKEPROFIT_HISTORY_SCHEMA_VERSION,
    predictions: [...history.predictions]
      .sort((a, b) => a.createdMs - b.createdMs)
      .slice(-MAX_HISTORY),
  };
}

/**
 * Records one observation per round (same "one per round, attach a late
 * quote" rule as V1's own recordObservation), computing this round's
 * take-profit target from V1's own baseProb at entry.
 */
export function recordTakeProfitObservation(
  history: TakeProfitHistory,
  response: Btc15PredictorResponse,
  config: TakeProfitConfig = DEFAULT_TAKE_PROFIT_CONFIG,
): TakeProfitHistory {
  const existingIndex = history.predictions.findIndex(
    (p) => p.roundId === response.round.id,
  );
  const quotePrice =
    response.quote.source === 'live_ask' &&
    validContractPrice(response.quote.contractPrice ?? 0)
      ? response.quote.contractPrice
      : null;

  if (existingIndex === -1) {
    const record: TakeProfitRecord = {
      roundId: response.round.id,
      createdMs: Date.parse(response.generatedAt),
      roundCloseMs: response.signal.roundCloseMs,
      roundOpenPrice: response.signal.roundOpenPrice,
      action: response.signal.action,
      probability: response.signal.baseProb,
      targetGain: takeProfitTarget(response.signal.baseProb, config),
      contractEntryPrice: quotePrice,
      contractQuoteSource: quotePrice !== null ? 'live_ask' : 'unavailable',
      targetLine: response.quote.targetLine ?? null,
      exitedEarly: false,
      exitPrice: null,
      exitMs: null,
      settled: false,
      settledMs: null,
      closePrice: null,
      actual: null,
      won: null,
    };
    return { ...history, predictions: [...history.predictions, record] };
  }

  const existing = history.predictions[existingIndex];
  if (existing.contractEntryPrice !== null || quotePrice === null) return history;

  const updated: TakeProfitRecord = {
    ...existing,
    contractEntryPrice: quotePrice,
    contractQuoteSource: 'live_ask',
  };
  const predictions = [...history.predictions];
  predictions[existingIndex] = updated;
  return { ...history, predictions };
}

/**
 * Marks a round's position sold early at `exitPrice` (the live bid at the
 * moment btc15-takeprofit-scheduled.ts observed the target had been hit).
 * A no-op if the round isn't recorded, is unfunded (no entry price — never
 * a real position to exit), or has already settled/exited — idempotent
 * under a duplicate or overlapping per-minute invocation, same as the
 * upsert-by-roundId pattern the rest of this history stack uses.
 */
export function markTakeProfitExitedEarly(
  history: TakeProfitHistory,
  roundId: string,
  exitPrice: number,
  exitMs: number,
): TakeProfitHistory {
  const index = history.predictions.findIndex((p) => p.roundId === roundId);
  if (index === -1) return history;

  const record = history.predictions[index];
  if (record.settled || record.exitedEarly || record.contractEntryPrice === null) {
    return history;
  }

  const updated: TakeProfitRecord = {
    ...record,
    exitedEarly: true,
    exitPrice,
    exitMs,
    settled: true,
    settledMs: exitMs,
    won: true,
  };
  const predictions = [...history.predictions];
  predictions[index] = updated;
  return { ...history, predictions };
}

/**
 * Settles the round immediately preceding the current one against the
 * Coinbase-close-price proxy, same source and rule as V1's own
 * settlePreviousRound — but only for a position that rode all the way to
 * settlement. A round that already exited early was settled at that
 * moment instead, so this is a no-op for it (checked via `settled`, same
 * idempotency guard the rest of this stack uses).
 */
export function settleTakeProfitRound(
  history: TakeProfitHistory,
  response: Btc15PredictorResponse,
): TakeProfitHistory {
  const previous = response.previousRound;
  if (!previous || previous.closePrice === null) return history;

  const index = history.predictions.findIndex((p) => p.roundId === previous.roundId);
  if (index === -1 || history.predictions[index].settled) return history;

  const record = history.predictions[index];
  const actual = proxyActual(previous.closePrice, record);
  const updated: TakeProfitRecord = {
    ...record,
    settled: true,
    settledMs: Date.now(),
    closePrice: previous.closePrice,
    actual,
    won: record.action === actual,
    settlementSource: 'coinbase_proxy',
  };
  const predictions = [...history.predictions];
  predictions[index] = updated;
  return { ...history, predictions };
}

/** An early exit's outcome was locked in by the sale itself, not by settlement. */
export function isTakeProfitEarlyExit(record: TakeProfitRecord): boolean {
  return record.exitedEarly;
}

/**
 * Same official re-grade as V1's applyOfficialResults, but only for
 * positions held to expiry — an early exit already realized its gain at
 * `exitPrice`, whatever the round later settled to.
 */
export function applyOfficialTakeProfitResults(
  history: TakeProfitHistory,
  results: Map<number, OfficialRoundResult>,
  nowMs: number = Date.now(),
): TakeProfitHistory {
  const predictions = applyOfficialResultsTo(
    history.predictions,
    results,
    nowMs,
    isTakeProfitEarlyExit,
  );
  return predictions === history.predictions ? history : { ...history, predictions };
}

export interface TakeProfitRecordStats {
  wins: number;
  losses: number;
  total: number;
  rate: number | null;
}

function settledRecords(
  history: TakeProfitHistory,
): Array<TakeProfitRecord & { won: boolean }> {
  return history.predictions
    .filter((p): p is TakeProfitRecord & { won: boolean } => p.settled && p.won !== null)
    .sort((a, b) => a.createdMs - b.createdMs);
}

function statsFor(records: Array<{ won: boolean }>, n: number): TakeProfitRecordStats {
  const xs = records.slice(-n);
  if (!xs.length) return { wins: 0, losses: 0, total: 0, rate: null };
  const wins = xs.filter((x) => x.won).length;
  return { wins, losses: xs.length - wins, total: xs.length, rate: wins / xs.length };
}

export interface TakeProfitAccuracyWindows {
  overall: TakeProfitRecordStats;
  windows: Array<{ n: number; stats: TakeProfitRecordStats }>;
}

const ACCURACY_WINDOW_SIZES = [10, 100, 250, 500, 1200];

export function computeTakeProfitAccuracyWindows(
  history: TakeProfitHistory,
): TakeProfitAccuracyWindows {
  const resolved = settledRecords(history);
  return {
    overall: statsFor(resolved, resolved.length),
    windows: ACCURACY_WINDOW_SIZES.map((n) => ({ n, stats: statsFor(resolved, n) })),
  };
}

export interface TakeProfitStrategyPnlResult {
  eligibleSignals: number;
  pricedSignals: number;
  trades: number;
  valueSkips: number;
  earlyExits: number;
  wins: number;
  losses: number;
  stake: number;
  /** Net of Kalshi's taker fee on every buy and on every early sale. */
  pnl: number;
  /** Kalshi taker fees paid across every funded trade (already subtracted from pnl). */
  feesPaid: number;

  roi: number | null;
  poolReturn: number | null;
  startingPool: number;
  endingPool: number;
}

/**
 * Replays quarter-Kelly sizing chronologically, same as computeStrategyPnl,
 * but an early-exit trade's payoff is the sale at `exitPrice`, not the full
 * binary settlement payout: selling `stake / entryPrice` contracts at
 * `exitPrice` returns `(stake / entryPrice) * exitPrice`, a smaller but
 * guaranteed gain rather than the full 1/entryPrice payout a held-to-expiry
 * win pays (or the total loss a held-to-expiry loss costs). Kalshi's taker
 * fee is charged on the buy and again on an early sale, so the formulas
 * above are applied net of fees (see packages/signal-engine/src/fees.ts).
 */
export function computeTakeProfitStrategyPnl(
  history: TakeProfitHistory,
  startingPool: number = DEFAULT_CONFIG.simulatedStartingPool,
  fees: FeeModel = KALSHI_TAKER_FEES,
): TakeProfitStrategyPnlResult {
  const ordered = history.predictions
    .filter((p) => p.settled)
    .sort((a, b) => a.createdMs - b.createdMs);

  let bankroll = startingPool;
  let stake = 0;
  let pnl = 0;
  let feesPaid = 0;
  let trades = 0;
  let wins = 0;
  let earlyExits = 0;
  let pricedSignals = 0;

  for (const prediction of ordered) {
    const entry = prediction.contractEntryPrice;
    if (entry === null || !validContractPrice(entry)) continue;
    pricedSignals++;

    const allocation = calculateAllocation(
      prediction.action,
      prediction.probability,
      entry,
      bankroll,
    );
    if (allocation.stake <= 0) continue;

    const exitPrice =
      prediction.exitedEarly && prediction.exitPrice !== null
        ? prediction.exitPrice
        : null;
    const tradePnl =
      exitPrice !== null
        ? earlyExitTradePnl(allocation.stake, entry, exitPrice, fees)
        : heldTradePnl(allocation.stake, entry, Boolean(prediction.won), fees);

    trades++;
    stake += allocation.stake;
    feesPaid += tradeFees(allocation.stake, entry, exitPrice, fees);
    pnl += tradePnl;
    bankroll += tradePnl;
    if (prediction.won) wins++;
    if (prediction.exitedEarly) earlyExits++;
  }

  return {
    eligibleSignals: ordered.length,
    pricedSignals,
    trades,
    valueSkips: pricedSignals - trades,
    earlyExits,
    wins,
    losses: trades - wins,
    stake,
    pnl,
    feesPaid,
    roi: stake ? pnl / stake : null,
    poolReturn: startingPool ? (bankroll - startingPool) / startingPool : null,
    startingPool,
    endingPool: bankroll,
  };
}

const takeProfitRecordSchema = z.object({
  roundId: z.string(),
  createdMs: z.number(),
  roundCloseMs: z.number(),
  roundOpenPrice: z.number(),
  action: z.enum(['UP', 'DOWN']),
  probability: z.number(),
  targetGain: z.number(),
  contractEntryPrice: z.number().nullable(),
  contractQuoteSource: z.enum(['live_ask', 'unavailable']),
  exitedEarly: z.boolean(),
  exitPrice: z.number().nullable(),
  exitMs: z.number().nullable(),
  settled: z.boolean(),
  settledMs: z.number().nullable(),
  closePrice: z.number().nullable(),
  actual: z.enum(['UP', 'DOWN']).nullable(),
  won: z.boolean().nullable(),
  targetLine: z.number().nullable().optional(),
  settlementSource: z.enum(['kalshi_official', 'coinbase_proxy']).optional(),
});

const takeProfitHistorySchema = z.object({
  schemaVersion: z.number(),
  predictions: z.array(takeProfitRecordSchema),
});

export interface ParseTakeProfitHistoryResult {
  ok: boolean;
  history: TakeProfitHistory | null;
  error: string | null;
}

export function parseTakeProfitHistory(raw: string): ParseTakeProfitHistoryResult {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, history: null, error: 'That data is not valid JSON.' };
  }

  const parsed = takeProfitHistorySchema.safeParse(json);
  if (!parsed.success) {
    return {
      ok: false,
      history: null,
      error:
        parsed.error.issues[0]?.message ??
        'That data does not match the expected schema.',
    };
  }

  return { ok: true, history: parsed.data, error: null };
}
